import { http } from "@/lib/http";

import type {
  Image,
  ImageCreateRequest,
  ImageImportRequest,
  ImageMember,
  ImageMemberUpdateRequest,
  ImageUpdateRequest,
  PaginatedImages,
} from "./types";

export interface ImageListFilters {
  /** Case-insensitive substring match on the image name. */
  name?: string;
  /** Case-insensitive exact status match (e.g. "active"). */
  status?: string;
}

export async function listImages(params: {
  page: number;
  pageSize: number;
  filters?: ImageListFilters;
}): Promise<PaginatedImages> {
  const { data } = await http.get<PaginatedImages>("/images", {
    params: {
      page: params.page,
      page_size: params.pageSize,
      // Filtered server-side before pagination; unknown params are ignored
      // until the backend support lands, so this degrades gracefully.
      name: params.filters?.name || undefined,
      status: params.filters?.status || undefined,
    },
  });
  return data;
}

export async function getImage(imageId: string): Promise<Image> {
  const { data } = await http.get<Image>(`/images/${imageId}`);
  return data;
}

export async function uploadImage(params: {
  file: File;
  metadata: ImageCreateRequest;
}): Promise<Image> {
  const form = new FormData();
  form.append("file", params.file);
  form.append("metadata", JSON.stringify(params.metadata));
  const { data } = await http.post<Image>("/images", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function updateImage(
  imageId: string,
  payload: ImageUpdateRequest,
): Promise<Image> {
  const { data } = await http.patch<Image>(`/images/${imageId}`, payload);
  return data;
}

export async function importImage(
  payload: ImageImportRequest,
): Promise<Image> {
  const { data } = await http.post<Image>("/images/import", payload);
  return data;
}

export async function deactivateImage(imageId: string): Promise<Image> {
  const { data } = await http.post<Image>(`/images/${imageId}/deactivate`);
  return data;
}

export async function reactivateImage(imageId: string): Promise<Image> {
  const { data } = await http.post<Image>(`/images/${imageId}/reactivate`);
  return data;
}

export async function downloadImage(imageId: string): Promise<Blob> {
  const { data } = await http.get(`/images/${imageId}/download`, {
    responseType: "blob",
  });
  return data as Blob;
}

export async function deleteImage(imageId: string): Promise<void> {
  await http.delete(`/images/${imageId}`);
}

// --- image members (sharing) --- //
export async function listImageMembers(
  imageId: string,
): Promise<ImageMember[]> {
  const { data } = await http.get<ImageMember[]>(`/images/${imageId}/members`);
  return data;
}

export async function addImageMember(
  imageId: string,
  memberId: string,
): Promise<ImageMember> {
  const { data } = await http.post<ImageMember>(`/images/${imageId}/members`, {
    member_id: memberId,
  });
  return data;
}

export async function updateImageMember(
  imageId: string,
  memberId: string,
  payload: ImageMemberUpdateRequest,
): Promise<ImageMember> {
  const { data } = await http.put<ImageMember>(
    `/images/${imageId}/members/${memberId}`,
    payload,
  );
  return data;
}

export async function removeImageMember(
  imageId: string,
  memberId: string,
): Promise<void> {
  await http.delete(`/images/${imageId}/members/${memberId}`);
}
